import { get } from 'lodash';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { SearchQuery } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { toSortParam } from '~/redux/api/utils';
import { initContext } from '~/redux/slices/documentsNavigation';
import { ColumnSortOrder } from '~/types/Columns';
import { parseNavigationResponseV3 } from '~/utils/navigation';

type Props = {
  sortBy: ColumnSortOrder;
  query: SearchQuery;
  pathname?: string;
  name?: string;
};

export const useSearchNavigation = ({
  sortBy,
  query,
  pathname = '/search',
  name = 'search results',
}: Props) => {
  const hasDeprecatePilotSearch = useFlag(FlagType.DeprecatePilotSearch);

  const [getPositionByHandlerEvisearch] =
    api.endpoints.getPositionByHandler.useLazyQuery();
  const [getHandlersByPositionEvisearch] =
    api.endpoints.getHandlersByPosition.useLazyQuery();
  const [getPositionByHandlerV3] =
    api.endpoints.getSearchV3PositionByHandler.useLazyQuery();
  const [getHandlersByPositionV3] =
    api.endpoints.getSearchV3HandlersByPosition.useLazyQuery();

  const dispatch = useDispatch();

  const queryRef = useRef<SearchQuery>();

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const navigationAPI = (position: number) => {
    const currentQuery = queryRef.current ?? [];
    if (hasDeprecatePilotSearch) {
      return getHandlersByPositionEvisearch({
        sort: toSortParam(sortBy),
        query: currentQuery,
        position,
      }).unwrap();
    }
    return getHandlersByPositionV3({
      sortBy,
      query: currentQuery,
      position,
    }).unwrap();
  };

  const getPosition = async (handler_id: number) => {
    const currentQuery = queryRef.current ?? [];
    if (hasDeprecatePilotSearch) {
      const response = await getPositionByHandlerEvisearch({
        sort: toSortParam(sortBy),
        handler_id,
        query: currentQuery,
      }).unwrap();
      return {
        position: get(response, 'data.attributes.position', -1),
        total: get(response, 'meta.total', 0),
      };
    }
    const response = await getPositionByHandlerV3({
      sortBy,
      handler_id,
      query: currentQuery,
    }).unwrap();
    return {
      position: get(response, 'data.attributes.position', -1),
      total: get(response, 'meta.total', 0),
    };
  };

  const handleNavigate = () => {
    const initOptions = {
      api: navigationAPI,
      getPosition,
      name,
      parseResponse: parseNavigationResponseV3,
      fromLocation: {
        pathname,
        hash: '',
        search: '',
      },
    };
    // @ts-ignore -- skip typing legacy code from redux upgrade
    dispatch(initContext(initOptions as any));
  };
  return [handleNavigate];
};
